import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { EnterKeyIcon } from '@repo/atomic-components';
import { useEditChatTitleMutation } from '@/api/chat';
import { TITLE_MAX_LENGTH } from '@/ui/features/chat/constants';

export interface EditableChatTitleProps {
  chatId: string;
  chatTitle: string;
  canEditChat: boolean;
  /** Fires whenever the input toggles between edit and view mode. */
  onEditingChange?: (isEditing: boolean) => void;
}

export function EditableChatTitle({ chatId, chatTitle, canEditChat, onEditingChange }: EditableChatTitleProps) {
  const [isEditing, setIsEditingState] = useState(false);
  const setIsEditing = (next: boolean) => {
    setIsEditingState(next);
    onEditingChange?.(next);
  };
  const [inputValue, setInputValue] = useState(chatTitle);
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  // Set when Enter, Escape or the save button ends the edit; a blur leaves focus where the user moved it.
  const shouldFocusEditButtonRef = useRef(false);
  const [editChatTitle, { isLoading }] = useEditChatTitleMutation();

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (shouldFocusEditButtonRef.current) {
      shouldFocusEditButtonRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    setInputValue(chatTitle);
    setIsEditing(true);
  };

  const saveTitle = async () => {
    const trimmed = inputValue.trim();
    if (trimmed && trimmed !== chatTitle && !isLoading) {
      try {
        await editChatTitle({ chatId, chatTitle: trimmed.slice(0, TITLE_MAX_LENGTH) }).unwrap();
      } catch {
        setInputValue(chatTitle);
      }
    } else {
      setInputValue(chatTitle);
    }
    setIsEditing(false);
  };

  const revertTitle = () => {
    setInputValue(chatTitle);
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      shouldFocusEditButtonRef.current = true;
      saveTitle();
    }
    if (e.key === 'Escape') {
      shouldFocusEditButtonRef.current = true;
      revertTitle();
    }
  };

  if (!chatTitle) {
    return null;
  }

  const isSubmitDisabled = !inputValue.trim().length;

  return (
    <div className="text-theme-black group/title relative flex min-w-0 shrink items-center gap-2">
      {isEditing ? (
        <div className="relative flex min-w-0 flex-1 items-center">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.slice(0, TITLE_MAX_LENGTH))}
            onBlur={revertTitle}
            onKeyDown={handleKeyDown}
            maxLength={TITLE_MAX_LENGTH}
            className={classNames(
              'text-theme-black bg-theme-white m-0 h-8 w-full min-w-0 font-sans text-[1rem] font-semibold @[480px]:text-[1.0625rem]',
              'appearance-none rounded-[4px] transition-colors duration-200',
              'border-theme-border-grey focus:border-theme-text-grey border',
              'outline-none focus:outline-none',
              'shadow-none focus:shadow-none',
              'py-1 pr-11 pl-2 @[480px]:min-w-112',
            )}
            aria-label="Edit chat title"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              shouldFocusEditButtonRef.current = true;
              saveTitle();
            }}
            disabled={isSubmitDisabled}
            aria-label="Save chat title"
            className={classNames(
              'absolute inset-y-0 right-2 my-auto flex h-4 w-4 items-center justify-center rounded-[3px] border',
              'bg-fletchers-gray-50 border-fletchers-gray-110 text-fletchers-gray-200',
              'hover:!bg-fletchers-gray-100 disabled:cursor-not-allowed disabled:opacity-40',
              !isSubmitDisabled && 'cursor-pointer',
            )}
          >
            <EnterKeyIcon />
          </button>
        </div>
      ) : (
        <>
          <h1
            className={classNames(
              'w-full min-w-0 shrink truncate text-[1rem] font-semibold @[480px]:text-[1.0625rem]',
              canEditChat && 'group-hover/title:text-theme-dark-green transition-colors',
            )}
          >
            {chatTitle}
          </h1>
          {/* Laid over the title so it stays a plain heading while a click on it still starts renaming. */}
          {canEditChat && (
            <button
              ref={editButtonRef}
              type="button"
              onClick={startEditing}
              aria-label={`Edit chat title: ${chatTitle}`}
              className="focus-visible:outline-fletchers-green-500 absolute inset-0 cursor-pointer rounded-[4px] border-none bg-transparent p-0"
            />
          )}
        </>
      )}
    </div>
  );
}
